import React, { useCallback } from 'react'
import {
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_HOTEL,
} from 'constants/offer'
import config from 'constants/config'
import { OverlayView, OverlayViewF } from '@react-google-maps/api'
import PropertyMarker from 'components/Common/PropertyMap/PropertyMarkerV2'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import Clickable from 'components/Common/Clickable'
import SearchResultsMapOfferView from 'components/Search/SearchResultsMap/SearchResultsMapOfferView'
import styled from 'styled-components'
import { rem } from 'polished'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { DESKTOP_TILE_WIDTH as MAP_TILE_MAX_WIDTH } from 'components/Search/SearchResultsMap/MapSearchTile/LeMapSearchTile'
import { TILE_HEIGHT as MAP_TILE_BEDBANK_HEIGHT } from 'components/Search/SearchResultsMap/MapSearchTile/BedbankMapSearchTile'
import { getOfferId } from 'components/Search/SearchResultsMap/utils'
import BusinessTravellerPropertyPriceMarker from './BusinessTravellerPropertyPriceMarker'

type Position = 'right' | 'left' | 'up' | 'down' | 'middle';

const mapTilePadding = 8

function getPositionAndOffsetOfTile(
  event: Event | React.MouseEvent<Element | MouseEvent>,
  containerRef: React.MutableRefObject<HTMLDivElement | null>,
  isBedbank: boolean,
): {
  position: Position | undefined;
  offset: { xOffset: number; yOffset: number } | undefined;
} {
  const maxWidth = MAP_TILE_MAX_WIDTH + 2 * mapTilePadding
  const flashTileHeight = 500
  // tile height is estimated since we don't know the tile height until it's rendered
  const maxHeight = (isBedbank ? MAP_TILE_BEDBANK_HEIGHT : flashTileHeight) + 2 * mapTilePadding
  const pin = event.currentTarget as HTMLDivElement
  const pinRect = pin.getBoundingClientRect()
  const target = event.target as HTMLDivElement
  const pinWidth = target.clientWidth
  const pinHeight = target.clientHeight
  const boundingRect = containerRef.current?.getBoundingClientRect()

  if (boundingRect) {
    const distanceLeft = pinRect.left - boundingRect?.left
    const distanceRight =
      boundingRect?.right - pinRect.right
    const distanceTop = pinRect.top - boundingRect?.top
    const distanceBottom =
      boundingRect?.bottom - pinRect.bottom
    // since tile would be displayed at bottom of pin, need to calculate distance from top of map to bottom of pin
    const distanceTopFromBottomOfPin = distanceTop + pinRect.height
    let position: Position | undefined
    let xOffset = 0
    let yOffset = 0
    const additionalOffsetFromBottom = 16

    // Check if there is enough space on the right
    if (distanceRight - maxWidth >= 0) {
      position = 'right'
    }
    // Check if there is enough space on the left
    else if (distanceLeft - maxWidth >= 0) {
      position = 'left'
    }
    // Check if there is enough space below
    else if (distanceBottom - maxHeight >= 0) {
      position = 'down'
    }
    // Check if there is enough space above
    else if (distanceTop - maxHeight >= 0) {
      position = 'up'
    }
    else {
      position = 'middle'
    }

    if (position === 'left' || position === 'right') {
      if (distanceTopFromBottomOfPin - maxHeight / 2 < 0) {
        // need to offset from top
        yOffset = maxHeight / 2 - distanceTopFromBottomOfPin
      } else if (distanceBottom - maxHeight / 2 < 0) {
        // need to offset from bottom
        yOffset = distanceBottom - maxHeight / 2 - additionalOffsetFromBottom
      }

      xOffset = pinWidth / 2
    }

    if (position === 'up' || position === 'down') {
      if (distanceLeft - maxWidth / 2 < 0) {
        // need to offset from left side
        xOffset = maxWidth / 2 - distanceLeft
      } else if (distanceRight - maxWidth / 2 < 0) {
        // need to offset from right side
        xOffset = distanceRight - maxWidth / 2
      }
      if (position === 'up') {
        yOffset = pinHeight
      }
    }

    if (position === 'middle') {
      if (distanceLeft - maxWidth / 2 < 0) {
        // need to offset from left side
        xOffset = maxWidth / 2 - distanceLeft
      } else if (distanceRight - maxWidth / 2 < 0) {
        // need to offset from right side
        xOffset = distanceRight - maxWidth / 2
      } else if (distanceTopFromBottomOfPin - maxHeight / 2 < 0) {
        // need to offset from top
        yOffset = maxHeight / 2 - distanceTopFromBottomOfPin
      } else if (distanceBottom - maxHeight / 2 < 0) {
        // need to offset from bottom
        yOffset = distanceBottom - maxHeight / 2 - additionalOffsetFromBottom
      }
    }
    const offset = { xOffset, yOffset }

    return {
      position,
      offset,
    }
  }

  return {
    position: undefined,
    offset: undefined,
  }
}

const MAX_PIN_LIMIT = config.search.mapView.pinLimit

const StyledContainer = styled.div<{
  xOffset: number;
  yOffset: number;
}>`
  position: absolute;
  z-index: 5;
  padding: ${rem(mapTilePadding)};
  &.position-left {
    transform: translate(
      calc(-100% - ${({ xOffset }) => xOffset}px),
      calc(-50% + ${({ yOffset }) => yOffset}px)
    );
  }
  &.position-right {
    transform: translate(
      calc(0% + ${({ xOffset }) => xOffset}px),
      calc(-50% + ${({ yOffset }) => yOffset}px)
    );
  }
  &.position-up {
    transform: translate(
      calc(-50% + ${({ xOffset }) => xOffset}px),
      calc(-100% - ${({ yOffset }) => yOffset}px)
    );
  }
  &.position-down {
    transform: translate(calc(-50% + ${({ xOffset }) => xOffset}px), 0%);
  }
  &.position-middle {
    transform: translate(
      calc(-50% + ${({ xOffset }) => xOffset}px),
      calc(-50% + ${({ yOffset }) => yOffset}px)
    );
  }
`

interface Props {
  onMarkerClick: (
    marker: App.SearchMapMarker,
    pinType: string,
    e?: Event | React.MouseEvent<Element | MouseEvent>
  ) => void;
  isActive: boolean;
  isHovered: boolean;
  isVisited: boolean;
  filters: App.OfferListFilters;
  onMarkerLoad?: (
    markerInstance: google.maps.Marker,
    markerData: App.SearchMapMarker
  ) => void;
  onMarkerUnmount?: (markerInstance: google.maps.Marker) => void;
  onDeselectOffers?: () => void;
  marker: App.SearchMapMarker;
  index: number;
  handleProductClick?: (
    offer:
      | App.Offer
      | App.OfferSummary
      | App.BedbankOffer
      | App.BedbankOfferSummary,
    position: number
  ) => void;
  mapContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  onHover?: (offerId: string) => void
  soldOut: boolean
  bestPrice?: number
  bestPriceDuration?: number
  bestPriceIsLoading?: boolean
}

function HotelMapMarker(props: Props) {
  const {
    marker,
    index,
    onMarkerClick,
    isActive,
    isVisited,
    filters,
    onDeselectOffers,
    handleProductClick,
    mapContainerRef,
    isHovered,
    onHover,
    soldOut,
    bestPrice,
    bestPriceDuration,
    bestPriceIsLoading,
  } = props

  const [tilePosition, setTilePosition] = React.useState<Position | undefined>(
    undefined,
  )
  const [tileOffset, setTileOffset] = React.useState<
    { xOffset: number; yOffset: number } | undefined
  >(undefined)

  const calculateZIndex = useCallback(
    (marker: App.SearchMapMarker, index: number) => {
      if (isActive) {
        return MAX_PIN_LIMIT + 1
      }
      if (isHovered) {
        return MAX_PIN_LIMIT
      }
      if (index >= MAX_PIN_LIMIT) {
        return 0
      }
      const offerType = marker.offers[0].type
      if (
        offerType === OFFER_TYPE_HOTEL ||
        offerType === OFFER_TYPE_ALWAYS_ON ||
        offerType === OFFER_TYPE_BED_BANK
      ) {
        return MAX_PIN_LIMIT - index
      }
      return 0
    },
    [isActive, isHovered],
  )

  const isMobile = useIsMobileScreen()

  const handleClick = useCallback(
    (e: Event | React.MouseEvent<Element | MouseEvent>) => {
      onMarkerClick(marker, index < MAX_PIN_LIMIT ? 'pricePin' : 'pill', e)
      if (!isMobile) {
        const { position, offset } = getPositionAndOffsetOfTile(
          e,
          mapContainerRef,
          marker.offers[0].type === OFFER_TYPE_BED_BANK,
        )
        setTilePosition(position)
        setTileOffset(offset)
      }
      e.stopPropagation()
    },
    [index, isMobile, mapContainerRef, marker, onMarkerClick],
  )

  const handleMouseEnter = useCallback(() => {
    if (onHover) {
      onHover(marker.id)
    }
  }, [marker.id, onHover])

  const handleMouseLeave = useCallback(() => {
    if (onHover) {
      onHover('')
    }
  }, [onHover])

  return (
    <OverlayViewF
      key={marker.id}
      position={{
        lat: marker.latitude ?? 0,
        lng: marker.longitude ?? 0,
      }}
      zIndex={calculateZIndex(marker, index)}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <Clickable onClick={handleClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {(config.businessTraveller.currentAccountMode !== 'business' || !config.BUSINESS_TRAVELLER_SPLIT_MAP_VIEW_UPDATES_ENABLED) && <PropertyMarker
          key={marker.id}
          marker={marker}
          active={isActive}
          isHovered={isHovered}
          visited={isVisited}
          scaleDown={isMobile}
          soldOut={soldOut}
        />}
        {config.businessTraveller.currentAccountMode === 'business' && config.BUSINESS_TRAVELLER_SPLIT_MAP_VIEW_UPDATES_ENABLED && !bestPriceIsLoading && <BusinessTravellerPropertyPriceMarker
          key={marker.id}
          isFlash={marker.offers[0].type === OFFER_TYPE_HOTEL}
          isActive={isActive || isHovered}
          isVisited={isVisited}
          bestPrice={bestPrice}
          bestPriceDuration={bestPriceDuration}
        />}
      </Clickable>
      <CSSBreakpoint min="tablet">
        {isActive && (
          <StyledContainer
            className={
              tilePosition !== undefined ? `position-${tilePosition}` : ''
            }
            xOffset={tileOffset?.xOffset ?? 0}
            yOffset={tileOffset?.yOffset ?? 0}
          >
            <SearchResultsMapOfferView
              key={marker.id}
              offerIds={marker.offers.map(getOfferId)}
              filters={filters}
              onProductClick={handleProductClick}
              onDeselectOffers={onDeselectOffers}
            />
          </StyledContainer>
        )}
      </CSSBreakpoint>
    </OverlayViewF>
  )
}

export default React.memo(HotelMapMarker)
